import { createContext, useContext, useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "./AuthContext";
const NotificationContext = createContext();

// Convert a URL-safe base64 VAPID key to the Uint8Array applicationServerKey format
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { getAuthHeaders, user } = useAuth();
  const [missingReports, setMissingReports] = useState([]);
  const [permissionStatus, setPermissionStatus] = useState("default");
  const [loading, setLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Check notification permission
  useEffect(() => {
    if ("Notification" in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
        .then((registration) => {
          console.log("Service Worker registered:", registration.scope);
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  // Detect push support + current subscription + standalone (installed) mode
  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const supported =
      "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setPushSupported(supported);

    if (supported) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushSubscribed(!!sub))
        .catch(() => {});
    }
  }, []);

  // Subscribe this device to web push notifications
  const subscribeToPush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      return { ok: false, reason: "unsupported" };
    }
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission !== "granted") return { ok: false, reason: "denied" };

      const { data } = await apiClient.get(`/push/vapid-public-key`);
      if (!data?.public_key) return { ok: false, reason: "not_configured" };

      const reg = await navigator.serviceWorker.ready;
      // Always re-register with the CURRENT server key. Reusing a stale
      // subscription (e.g. created with an old key) causes the push service to
      // silently reject deliveries, so we drop any existing one first.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try {
          await apiClient.post(`/push/unsubscribe`, { endpoint: existing.endpoint });
        } catch (e) {}
        try {
          await existing.unsubscribe();
        } catch (e) {}
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await apiClient.post(`/push/subscribe`, { subscription: sub.toJSON() });
      setPushSubscribed(true);
      return { ok: true };
    } catch (error) {
      console.error("Push subscription failed:", error);
      return { ok: false, reason: "error" };
    }
  }, []);

  // Unsubscribe this device from web push notifications
  const unsubscribeFromPush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await apiClient.post(`/push/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushSubscribed(false);
      return { ok: true };
    } catch (error) {
      console.error("Push unsubscribe failed:", error);
      return { ok: false };
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      return permission === "granted";
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, []);

  // Fetch missing reports
  const fetchMissingReports = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/notifications/missing-reports`);
      setMissingReports(response.data.missing_reports || []);
      return response.data;
    } catch (error) {
      console.error("Error fetching missing reports:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  // Show browser notification
  const showNotification = useCallback((title, options = {}) => {
    if (permissionStatus !== "granted") {
      console.log("Notifications not permitted");
      return;
    }

    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      // Use service worker for notification (iOS-safe options only)
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          icon: "/logo.png",
          tag: "v2r-alert",
          ...options,
        });
      });
    } else if ("Notification" in window) {
      // Fallback to regular notification
      new Notification(title, {
        icon: "/logo.png",
        ...options,
      });
    }
  }, [permissionStatus]);

  // Check if it's notification time and show alerts
  const checkAndNotify = useCallback(async () => {
    if (!user || permissionStatus !== "granted") return;

    try {
      const [timeResponse, reportsData] = await Promise.all([
        apiClient.get(`/notifications/check-time`),
        fetchMissingReports(),
      ]);

      if (timeResponse.data.is_notification_time && reportsData?.missing_reports?.length > 0) {
        const count = reportsData.missing_reports.length;
        const jobNames = reportsData.missing_reports.slice(0, 3).map(r => r.job_name).join(", ");
        
        showNotification("Daily Report Reminder", {
          body: count === 1 
            ? `Missing daily report for: ${jobNames}`
            : `${count} jobs missing daily reports: ${jobNames}${count > 3 ? "..." : ""}`,
          data: { url: "/" },
        });
      }
    } catch (error) {
      console.error("Error checking notifications:", error);
    }
  }, [user, permissionStatus, getAuthHeaders, fetchMissingReports, showNotification]);

  // Set up periodic check (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    fetchMissingReports();

    // Check every 5 minutes
    const interval = setInterval(() => {
      checkAndNotify();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, fetchMissingReports, checkAndNotify]);

  const value = {
    missingReports,
    permissionStatus,
    loading,
    pushSupported,
    pushSubscribed,
    isStandalone,
    subscribeToPush,
    unsubscribeFromPush,
    requestPermission,
    fetchMissingReports,
    showNotification,
    checkAndNotify,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
